import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { Grid } from 'semantic-ui-react'

import { Alert, Card, GridRowColumn, Popup, Text } from '@/base'
import { useReselector } from '@/utils/sharedHooks'
import { selectIsCatchUpBookkeepingComplete } from '@/featuresV1/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import {
  selectLastYearExpenseRatio,
  selectLastYearExpenses,
  selectProjectedExpenses,
  selectYtdExpenseRatio,
  selectYtdExpenses,
} from '@/featuresV1/GoalSetting/goals.selectors'
import { formatCurrencyWithDefault } from '@/featuresV1/GoalSetting/helpers'

const ExpensesTab = () => {
  const catchUpBookkeepingComplete = useReselector(
    selectIsCatchUpBookkeepingComplete
  )
  const currentYear = useMemo(() => DateTime.now().year, [])
  const ytdExpenses = useReselector(selectYtdExpenses)
  const projectedExpenses = useReselector(selectProjectedExpenses)
  const lastYearExpenses = useReselector(selectLastYearExpenses)
  const expenseRatio = useReselector(selectYtdExpenseRatio)
  const lastYearExpenseRatio = useReselector(selectLastYearExpenseRatio)

  return (
    <Grid>
      <GridRowColumn>
        <Text as="h2">Expense Goal Help</Text>
      </GridRowColumn>
      <GridRowColumn>
        <Text>
          Expenses are the costs of running your business. Setting an expense
          target helps you manage spending. Comparing your expenses to your
          income (your “expense ratio”) is a helpful way to benchmark how much
          you&apos;re spending versus how much you&apos;re bringing in.
        </Text>
      </GridRowColumn>
      <GridRowColumn>
        <Alert type="warn">
          Most therapy practices keep their expense ratios between 20-40%.
        </Alert>
      </GridRowColumn>
      <GridRowColumn>
        <Text>
          Based upon your current performance, we’ve projected your expenses for
          the year to help you set a goal.
        </Text>
      </GridRowColumn>
      <GridRowColumn>
        <Card type="subsection" backgroundColor="stone40">
          <Grid>
            <GridRowColumn>
              <Text as="h3">This Year’s Expenses</Text>
            </GridRowColumn>
            <Grid.Row>
              <Grid.Column width={8}>
                <Text as="eyebrow" color="darkGray">
                  projected {currentYear} expenses
                  <Popup
                    content={
                      <>
                        <Text as="h3">Projected Expenses</Text>
                        <br />
                        <Text>
                          This is calculated by taking how much you&apos;ve
                          spent this year, and dividing it by how many days have
                          already passed. Then, to project out for the year, you
                          multiply this by the number of days in a year (365).
                          <br />
                          <br />
                          For example, as of May 10, you would be 130 days into
                          the year. If your expenses total $13,000 so far,
                          divide $13,000/130 days. This calculates to a rate of
                          $100/day.
                          <br />
                          <br />
                          To project out for the rest of the year, multiply that
                          rate of $100/day by the number of days per year. This
                          would give you projected expenses of $36,500.
                        </Text>
                      </>
                    }
                  />
                </Text>
                <br />
                <Text as="h2">
                  {formatCurrencyWithDefault(
                    projectedExpenses.multiply(-1),
                    catchUpBookkeepingComplete
                  )}
                </Text>
                <br />
                <Text as="bodySm" color="darkGray">
                  Based on YTD expenses of{' '}
                  {formatCurrencyWithDefault(
                    ytdExpenses * -1,
                    catchUpBookkeepingComplete
                  )}
                </Text>
              </Grid.Column>
              <Grid.Column width={8}>
                <Text as="eyebrow" color="darkGray">
                  ytd expense ratio
                  <Popup
                    content={
                      <>
                        <Text as="h3">Year to Date Expense Ratio</Text>
                        <br />
                        <Text>
                          This is calculated by the amount you&apos;ve spent on
                          expenses this year divided by your year to date
                          income, as categorized by your Heard bookkeeping team.
                        </Text>
                      </>
                    }
                  />
                </Text>
                <br />
                <Text as="h2">
                  {catchUpBookkeepingComplete ? expenseRatio : '-'}%
                </Text>
              </Grid.Column>
            </Grid.Row>
            {!catchUpBookkeepingComplete && (
              <GridRowColumn>
                <Alert type="warn">
                  We’ll be able to provide you with a projected expenses once
                  your catch-up bookkeeping is complete.
                </Alert>
              </GridRowColumn>
            )}
          </Grid>
        </Card>
      </GridRowColumn>
      {catchUpBookkeepingComplete && (
        <GridRowColumn>
          <Card type="subsection" backgroundColor="stone40">
            <Grid>
              <GridRowColumn>
                <Text as="h3">Last Year’s Expenses</Text>
              </GridRowColumn>
              <Grid.Row>
                <Grid.Column width={8}>
                  <Text as="eyebrow" color="darkGray">
                    {currentYear - 1} expenses
                  </Text>
                  <br />
                  <Text as="h2">
                    {formatCurrencyWithDefault(lastYearExpenses * -1)}
                  </Text>
                </Grid.Column>
                <Grid.Column width={8}>
                  <Text as="eyebrow" color="darkGray">
                    {currentYear - 1} expenses ratio
                  </Text>
                  <br />
                  <Text as="h2">{lastYearExpenseRatio}%</Text>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Card>
        </GridRowColumn>
      )}
    </Grid>
  )
}

export default ExpensesTab
